import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { onAttributeSubmit } from "../../controller/AttributeController";
import 'react-toastify/dist/ReactToastify.css';
import toast, { Toaster } from 'react-hot-toast';
import AuthUtility from "../../util/AuthUtility";
import { Col, Button, Row, Container, Card, Form, InputGroup, Table, ListGroup, ListGroupItem, Badge } from 'react-bootstrap';
import { AttributeEntity } from "../../dto/attribute/AttributeEntity";
import { AttributeChoiceEntity } from "../../dto/attribute/AttributeChoiceEntity";

const Attribute = () => {
  const navigate = useNavigate();

  const initialAttributeState: AttributeEntity = {
    code: "",
    name: "",
    dataType: "TEXT",
    uiType: "TEXTBOX",
    status: "ACTIVE",
    verificationRequired: false,
    choices: []
  };

  const [attribute, setAttribute] = useState<AttributeEntity>(initialAttributeState);

  const [choice, setChoice] = useState<AttributeChoiceEntity>({
      status: "ACTIVE",
      sortOrder: 1
  });

  useEffect(() => {
    if (!AuthUtility.isUserLoggedIn()) {
        navigate("/login");
        window.location.reload();
    }
  }, [navigate]);

  const addChoice = () => {
    if (!choice.valueText) return;
    
    setAttribute(prev => ({
        ...prev,
        choices: [...(prev.choices || []), choice]
    }));
    
    setChoice({
        status: "ACTIVE"
    });
  };

  const removeChoice = (index: number) => {
    setAttribute(prev => ({
      ...prev,
      choices: prev.choices?.filter((_, i) => i !== index)
    }));
  };

  const attributeSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
  
    try {
      const response = await onAttributeSubmit(attribute);
      if (response.status === 0) {
        toast.success(response.msg);

        // 🔥 RESET FORM (like reload, but clean)
        setAttribute(initialAttributeState);

        // also reset choice temp state if you use it
        setChoice({
          status: "ACTIVE",
          sortOrder: 1
        });
      } else {
        toast.error(response.msg);
      }
    } catch {
      toast.error("Амжилтгүй");
    }
  };

  return (
    <Container fluid>
        <Toaster position="top-right" />
      <h3>
        <a onClick={() => navigate(-1)} style={{ cursor: "pointer" }}>
          ←
        </a>{" "}
        Атрибут
      </h3>

      <Form onSubmit={attributeSubmit}>
        {/* CODE */}
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>CODE</Form.Label>
              <Form.Control
                size="sm"
                value={attribute.code || ""}
                onChange={e => setAttribute({ ...attribute, code: e.target.value })}
                required
              />
            </Form.Group>
          </Col>
        </Row>

        {/* NAME */}
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>NAME</Form.Label>
              <Form.Control
                size="sm"
                value={attribute.name || ""}
                onChange={e => setAttribute({ ...attribute, name: e.target.value })}
                required
              />
            </Form.Group>
          </Col>
        </Row>

        {/* DATA TYPE */}
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>DATA TYPE</Form.Label>
              <Form.Select
                size="sm"
                value={attribute.dataType}
                onChange={e => setAttribute({ ...attribute, dataType: e.target.value })}
                required
              >
                <option value="TEXT">TEXT</option>
                <option value="NUMBER">NUMBER</option>
                <option value="BOOLEAN">BOOLEAN</option>
                <option value="CHOICE">CHOICE</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* UI TYPE */}
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>UI TYPE</Form.Label>
              <Form.Select
                size="sm"
                 value={attribute.uiType}
                onChange={e => setAttribute({ ...attribute, uiType: e.target.value })}
                required
              >
                <option value="TEXTBOX">TEXTBOX</option>
                <option value="DROPDOWN">DROPDOWN</option>
                <option value="COLOR">COLOR</option>
                <option value="IMAGE">IMAGE</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* STATUS */}
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>STATUS</Form.Label>
              <Form.Select
                size="sm"
                value={attribute.status || "ACTIVE"}
                onChange={e => setAttribute({ ...attribute, status: e.target.value })}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="HIDDEN">HIDDEN</option>
                <option value="DISABLED">DISABLED</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* VERIFICATION REQUIRED */}
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>VERIFICATION REQUIRED</Form.Label>
              <Form.Select
                size="sm"
                value={attribute.verificationRequired ? "true" : "false"}
                onChange={e =>
                  setAttribute({
                    ...attribute,
                    verificationRequired: e.target.value === "true"
                  })
                }
              >
                <option value="false">FALSE</option>
                <option value="true">TRUE</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* CHOICES */}
        <Row className="mb-3">
            <Col md={3}>
                <Form.Group>
                <Form.Label>VALUE</Form.Label>
                <Form.Control
                    size="sm"
                    value={choice.valueText || ""}
                    onChange={e => setChoice({ ...choice, valueText: e.target.value })}
                />
                </Form.Group>
            </Col>

            <Col md={3}>
                <Form.Group>
                <Form.Label>STATUS</Form.Label>
                <Form.Select
                    size="sm"
                    value={choice.status}
                    onChange={e => setChoice({ ...choice, status: e.target.value })}
                >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="HIDDEN">HIDDEN</option>
                    <option value="DISABLED">DISABLED</option>
                </Form.Select>
                </Form.Group>
            </Col>

            <Col md={3}>
                <Form.Group>
                <Form.Label>SORT ORDER</Form.Label>
                <Form.Control
                    size="sm"
                    type="number"
                    value={choice.sortOrder}
                    onChange={e => setChoice({ ...choice, sortOrder: Number(e.target.value) })}
                />
                </Form.Group>
            </Col>

            <Col md={2} className="d-flex align-items-end">
                <Button size="sm" onClick={addChoice}>
                Add
                </Button>
            </Col>
        </Row>

        <Row className="mb-3">
            <Col md={6}>
                <ListGroup>
                    {attribute.choices?.map((c, i) => (
                        <ListGroup.Item
                        key={i}
                        className="d-flex justify-content-between align-items-center"
                        >
                        <div>
                            <strong>{c.valueText}</strong>
                            <div className="text-muted">
                            Status: {c.status} | Order: {c.sortOrder}
                            </div>
                        </div>
                        <Button
                            size="sm"
                            variant="danger"
                            onClick={() => removeChoice(i)
                              // setAttribute(prev => ({
                              //     ...prev,
                              //     choices: prev.choices?.filter((_, idx) => idx !== i)
                              // }))
                            }
                        >
                            Remove
                        </Button>
                    </ListGroup.Item>
                ))}
                </ListGroup>
            </Col>
        </Row>

        {/* SAVE */}
        <Row>
          <Col md={3}>
            <Button size="sm" type="submit">
              Хадгалах
            </Button>
          </Col>
        </Row>
      </Form>
    </Container>
  )
}

export default Attribute;